<script setup lang="ts">
  import { computed } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';

  import {
    ThemeProvider,
    IconImage,
    FormInput,
    PositionUserSelector,
  } from '@/shared/ui';
  import {
    NotificationResponse,
    formatCivilDateToPtBR,
  } from '@/shared/lib';
  import type { NotificationUser } from '@/shared/lib';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';

  import { getPeriodicityLabel } from '../model/periodicity';

  const props = defineProps<{
    notification: NotificationResponse;
  }>();

  const emit = defineEmits<{
    (e: 'closeModal'): void;
  }>();

  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return 'Não definida';
    const formatted = formatCivilDateToPtBR(dateStr);
    return formatted === '-' ? 'Não definida' : formatted;
  };

  const hasCustomDates = computed(() => !!props.notification.customDates?.length);

  const selectedUsers = computed(() =>
    props.notification.users?.data.map((user: NotificationUser) => user.id.toString()) ?? []
  );

  const selectedPositions = computed(() => {
    const positions: { id: number; name: string }[] = [];
    props.notification.users?.data.forEach((user: NotificationUser) => {
      user.userPositions.forEach((userPosition) => {
        if (userPosition.position && !positions.find((p) => p.id === userPosition.position.id)) {
          positions.push(userPosition.position);
        }
      });
    });
    return positions.map((position) => position.id.toString());
  });

  const positionsData = computed(() => {
    const positions: { id: number; name: string }[] = [];
    props.notification.users?.data.forEach((user: NotificationUser) => {
      user.userPositions.forEach((userPosition) => {
        if (userPosition.position && !positions.find((p) => p.id === userPosition.position.id)) {
          positions.push(userPosition.position);
        }
      });
    });
    return { success: true, data: positions };
  });

  const getPeriodicityDisplayValue = computed(() =>
    getPeriodicityLabel(props.notification.periodicity, props.notification.customDays),
  );

  const handleClose = () => emit('closeModal');
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="bg-white-01 rounded-2xl shadow-lg w-[1264px] max-h-[90vh] overflow-auto"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <ThemeProvider>
      <div class="flex flex-col h-full">
        <!-- Header -->
        <div class="pl-6 pr-10 py-3 border-b border-gray-02">
          <div class="flex justify-between items-center">
            <h2 class="font-semibold text-gray-07">
              Visualizar Notificação
            </h2>
            <button
              class="text-gray-400 hover:text-gray-600 transition-colors"
              @click="handleClose"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z"
                  fill="var(--color-gray-07)"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto">
          <!-- Título da Notificação -->
          <div class="px-6 py-8 border-b border-gray-02">
            <FormInput
              id="notification-title"
              :model-value="notification.title"
              input-class="bg-gray-13"
              :disabled="true"
              label="Título da Notificação:"
              type="text"
              placeholder=""
            />
          </div>

          <!-- Periodicidade -->
          <div class="px-6 py-8 border-b border-gray-02">
            <FormInput
              id="notification-periodicity"
              :model-value="getPeriodicityDisplayValue"
              input-class="bg-gray-13"
              :disabled="true"
              label="Periodicidade:"
              type="text"
              placeholder=""
            />
          </div>

          <!-- Datas personalizadas -->
          <div
            v-if="hasCustomDates"
            class="px-6 py-8 border-b border-gray-02"
          >
            <h4 class="font-semibold text-gray-07 text-sm h-8 flex items-center">
              Datas:
            </h4>
            <div class="flex flex-col gap-3 mt-2">
              <div
                v-for="(date, index) in notification.customDates"
                :key="index"
                class="flex items-center gap-2 h-[42px] px-4 py-2 bg-gray-13 border border-gray-02 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] w-fit"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendário"
                  class="h-6 w-6 shrink-0"
                />
                <span class="text-sm font-medium text-gray-07">{{ formatDate(date) }}</span>
              </div>
            </div>
          </div>

          <!-- Datas padrão (início e fim) -->
          <div
            v-else
            class="px-6 py-8 border-b border-gray-02 flex gap-8 items-start"
          >
            <div class="flex flex-col gap-2">
              <h4 class="font-semibold text-gray-07 text-sm h-8 flex items-center">
                Data de início:
              </h4>
              <div
                class="flex items-center gap-2 h-[42px] px-4 py-2 bg-gray-13 border border-gray-02 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] w-[182px]"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendário"
                  class="h-6 w-6 shrink-0"
                />
                <span class="text-sm font-medium text-gray-07">{{ formatDate(notification.startDate) }}</span>
              </div>
            </div>

            <div class="flex flex-col gap-2">
              <h4 class="font-semibold text-gray-07 text-sm h-8 flex items-center">
                Data final:
              </h4>
              <div
                class="flex items-center gap-2 h-[42px] px-4 py-2 bg-gray-13 border border-gray-02 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] w-[182px]"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendário"
                  class="h-6 w-6 shrink-0"
                />
                <span class="text-sm font-medium text-gray-07">{{ formatDate(notification.endDate) }}</span>
              </div>
            </div>
          </div>

          <!-- Colaboradores selecionados -->
          <div class="px-6 pt-8 pb-8 border-b border-gray-02">
            <h4 class="font-semibold text-gray-07 text-sm h-8 flex items-center pb-3">
              Colaboradores selecionados:
            </h4>
            <PositionUserSelector
              :positions="positionsData"
              :users="{ success: true, data: (notification.users.data || []) as any }"
              :selected-positions="selectedPositions"
              :selected-users="selectedUsers"
              :positions-selected-count="positionsData.data.length"
              :users-selected-count="notification.users?.data?.length ?? 0"
              :hide-selection="true"
              selection-class="bg-gray-13"
            />
          </div>

          <!-- Texto da Notificação -->
          <div class="px-6 py-8">
            <h4 class="font-semibold text-gray-07 text-sm h-8 flex items-center">
              Texto da Notificação:
            </h4>
            <textarea
              id="notification-message"
              :value="notification.message"
              :disabled="true"
              placeholder=""
              class="w-full h-[134px] border border-gray-02 rounded-lg px-4 py-3 text-sm text-gray-07 resize-none shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] bg-gray-13 focus:outline-none mt-1"
            />
          </div>
        </div>
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>

<style scoped>
  .vfm-fade-enter-active,
  .vfm-fade-leave-active {
    transition: opacity 0.2s;
  }

  .vfm-fade-enter-from,
  .vfm-fade-leave-to {
    opacity: 0;
  }
</style>
